export const WEAPON = {
  id: 'revolver',
  label: 'R-44 Talon',
  class: 'pistol',
  caliber: '.44 Magnum',
  /** Niche, high-skill secondary — above the free starter pistol, in the
   * same band as the shotgun (1200). */
  price: 1200,
  /* Double-action trigger pull, not a mechanical rate of fire — `rpm` here
   * stands in for how fast a shooter can stage and break a heavy DA pull
   * six times in a row, the same convention `sniper`/`shotgun` use for their
   * manual cycles. 150 rpm = 0.4 s between shots. */
  rpm: 150,
  modes: ['semi'],
  burstCount: 1,
  burstRpm: 150,
  burstDelay: 0.1,
  /* Six rounds in the cylinder, not a magazine — see models/revolver.js's
   * header comment for how the "no detachable magazine" fields below are
   * handled. `magSize` is still the honest count of "rounds before reload". */
  magSize: 6,
  /* Four cylinders' worth carried loose/on a belt slide, not spare mags. */
  reserve: 24,
  /* .44 Magnum, 240gr JHP from an 8-3/8" barrel: real factory ballistics run
   * ~410 m/s at that barrel length (a 4" gun is closer to 330-360 m/s — the
   * long barrel is doing real ballistic work here, not just a bigger prop). */
  muzzleVelocity: 410,
  /* Hardest-hitting handgun in the game by design: clearly above the 9x19
   * SMG's 24-per-hit, and reaching toward what a shotgun does at point-blank
   * (18 per pellet x up to 8 pellets), without matching the precision
   * rifle's 95 (this is still a fast-follow-up-capable sidearm, not a bolt
   * gun). */
  damage: 65,
  /* A heavy, deep-penetrating flat-nosed magnum bullet — real .44 Magnum has
   * a reputation for barrier penetration well above typical 9x19/.45 ACP
   * despite modest velocity, because of its bullet mass and sectional
   * density. Below the 5.56 rifle round's 1.0 (still a handgun cartridge)
   * but well above the 9x19 pistol's 0.35. */
  penetration: 0.85,
  /* Big slow bullet sheds energy faster than a rifle round past its short
   * effective range, but not much faster than a 9x19 does. */
  dropoff: 0.55,
  maxRange: 160,
  dragK: 0.5,
  /* Only six rounds — every one gets a tracer, the same call shotgun.js
   * makes for its 8-round tube. */
  tracerEvery: 1,
  /* Long sight radius (adjustable rear + ramp front over an 8-3/8" barrel)
   * tightens hipfire and ADS versus the compact pistol, but the heavy DA
   * trigger and the biggest per-shot recoil in the non-sniper roster both
   * push spreadPerShot/spreadMax up. */
  spreadHip: 2.6,
  spreadAds: 0.3,
  spreadPerShot: 0.6,
  spreadMax: 4.4,
  spreadDecay: 3.0,
  /* Biggest recoil numbers of any non-sniper weapon (checked against every
   * other def: the shotgun's pitch 0.019/kickBack 0.03/kickUp 0.013/roll
   * 0.05/punch 0.6 was the previous high-water mark on every axis; every
   * field below is set above it). */
  recoil: {
    pitch: 0.024,
    yaw: 0.007,
    kickBack: 0.034,
    kickUp: 0.017,
    roll: 0.06,
    punch: 0.7,
    freq: 6.0,
    damping: 0.38,
    patternLength: 6,
    patternSeed: 0x445301,
    climbShape: [1.5, 1.3, 1.15, 1.05, 1.0],
    drift: 1.0,
  },
  adsTime: 0.24,
  adsFov: 0.85,
  viewFov: 0.92,
  /* A plain press-check reload — six loose rounds pressed into the cylinder
   * one at a time, no speedloader animation. Unlike an auto pistol (whose
   * reloadTac/reloadEmpty differ by more than half a second because a
   * partial mag swap is much faster than seating a fresh one from slide-
   * lock), reloading a revolver takes about the same effort whether one
   * chamber or all six are empty, so the two numbers sit close together. */
  reloadTac: 2.7,
  reloadEmpty: 3.0,
  inspectTime: 2.8,
  drawTime: 0.5,
  holsterTime: 0.35,
  /* --- pose ---
   * Same method as defs/rifle.js's derivation (solved from the bore axis,
   * not from where anything "looks nice") — see the long comment there for
   * the reasoning this reproduces. Checked with the actual projection
   * arithmetic (Euler 'XYZ' composition + the 60 deg-vertical-FOV pixel
   * mapping rifle.js's comment documents), not eyeballed:
   *
   *   muzzle_local = (0, 0.046, -0.2445), sight_local (front post) =
   *   (0, 0.067, -0.226). With the hipPos/hipRot below those project to
   *   roughly (1124, 725) and (1135, 696) at 1920x1080 — inside the same
   *   "muzzle up-left, heading for the crosshair" window rifle.js's own
   *   solve targets (1050-1300, 620-780) — while the cylinder/frame mass
   *   (near-origin, z=-0.008) lands around (1274, 817) and the firing-hand
   *   grip around (1472, 1019), both comfortably in the lower-right frame
   *   the way pistol.js's shipped pose also lands its magSeat/gripR
   *   ((1300, 956) / (1468, 1045)) — so this reads as the same class of
   *   two-handed extended-arm hold, just a longer gun.
   *
   * hipPos.z is pushed 30 mm further out than pistol.js's -0.34: the barrel
   * is 110 mm longer than the pistol's slide, and without backing off, the
   * muzzle would run too far up-frame the same way rifle.js's superseded
   * pose (z=-0.215) put the old carbine's muzzle off the top-left edge.
   *
   * NOT capture-verified — this session cannot run the Playwright-based
   * `tools/shoot.mjs` rig, so the arithmetic above is checked but the actual
   * on-screen framing is not. */
  hipPos: [0.116, -0.152, -0.35],
  hipRot: [-0.048, 0.069, -0.12],
  adsCant: [0, 0, 0.003],
  /* Two-handed extended-arm hold, same class and stance as the base pistol,
   * so the same eye relief: nothing about a longer/heavier gun changes how
   * far the shooter's arms extend. */
  eyeRelief: 0.34,
  /* Sprint/low-ready: carried over by the SAME DELTA from pistol.js's own
   * hip->sprint/low-ready offsets (the method rifle.js's comment describes
   * for its own sprint pose), so the blend does not translate the weapon
   * sideways on the way into a sprint. */
  sprintPos: [0.091, -0.252, -0.29],
  sprintRot: [-0.418, 0.503, 0.135],
  lowReadyPos: [0.101, -0.262, -0.33],
  lowReadyRot: [-0.438, 0.108, -0.075],
  /* Heavier all-steel N-frame gun: more perceived sway on its own inertia,
   * slightly less step-bob once gripped with both hands. */
  swayScale: 1.2,
  bobScale: 1.05,
  /* No detachable magazine (see models/revolver.js) — `magLen` is read
   * unconditionally by clips.js as a hand-choreography reach distance for
   * the generic reload mime, not a literal prop measurement (shotgun.js's
   * own magLen, 0.24, is already far from its tube's real length for the
   * same reason). Set smaller than the pistol's 0.108: there is no
   * magazine body to travel with, just a fistful of loose rounds. */
  magLen: 0.09,
};

export const loadModel = () => import('../models/revolver.js').then((m) => m.buildRevolver);
